import {
  laptopMaxXL,
  laptopMinXL,
  largeTabletMax,
  largeTabletMin,
  mobileXXLargeMax,
  tabletMax,
} from '@propertypal/shared/src/constants/mediaQueries';
import styled, { css } from 'styled-components';
import { Container as Button } from '../buttons/Button.style';
import FontAwesomeIcon from '../icons/FontAwesomeIcon';
import { SmallHeading } from '../typography';
import { ProductScreenBadge } from './ProductScreenTabs';

const TAB_Y_PADDING = 20;

type BreakpointProps = { breakpoint?: number; min?: boolean; fb?: string };

const bp = ({ breakpoint, min = false, fb }: BreakpointProps) => {
  const fallback = min ? largeTabletMin : largeTabletMax;
  return breakpoint ? `${min ? 'min' : 'max'}-width: ${breakpoint + (min ? 1 : 0)}px` : fb || fallback;
};

export const Container = styled.section<{ pb?: number; breakpoint?: number; mobPadding?: boolean }>`
  background: ${({ theme }) => theme.backgroundLightest};
  padding: 40px 20px ${({ pb }) => pb || 70}px 20px;

  @media (${({ breakpoint }) => bp({ breakpoint })}) {
    padding-top: ${({ mobPadding }) => (mobPadding ? 40 : 0)}px;
  }
`;

export const Wrapper = styled.div<{ maxW?: number }>`
  margin: 0 auto;
  max-width: ${({ maxW }) => maxW || 1110}px;
`;

export const MockupContainer = styled.div`
  position: relative;
  width: 100%;
  margin: 0 auto;

  @media (${tabletMax}) {
    max-width: 80vw;
  }
`;

export const TabsContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

export const TabsWrapper = styled.div`
  display: flex;
  gap: 15px;
  flex-direction: column;
  position: relative;
`;

export const CheckIcon = styled(FontAwesomeIcon)`
  color: ${({ theme }) => theme.primary};
`;

export const NavIcon = styled.button`
  color: ${({ theme }) => theme.primary};
  transition: opacity 300ms linear;
  background: ${({ theme }) => theme.white};
  align-self: flex-end;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const StaticContent = styled.div`
  padding: 10px 20px;
`;

export const TabContent = styled.div<{ active: boolean; breakpoint?: number; staticTabs?: boolean }>`
  background-color: ${({ active }) => (active ? '#fcded0' : 'transparent')};
  padding: ${TAB_Y_PADDING}px 10px ${TAB_Y_PADDING}px 20px;
  border-radius: 8px;
  transition:
    background-color 300ms linear,
    box-shadow 300ms linear;

  ${({ active }) =>
    active
      ? css`
          box-shadow: 0 0 8px 0 rgba(0, 0, 0, 0.1);
          z-index: 1;
        `
      : ''}

  ${NavIcon} {
    opacity: ${({ active }) => (active ? 1 : 0)};
    width: 44px;
    height: 44px;
  }

  @media (${({ breakpoint }) => bp({ breakpoint, min: true, fb: largeTabletMin })}) {
    ${CheckIcon} {
      opacity: ${({ active }) => (active ? 1 : 0)};
      font-size: ${({ active }) => (active ? 35 : 0)}px;
      transition:
        opacity 300ms linear,
        font-size 100ms linear;

      path {
        ${({ active }) =>
          !active
            ? css`
                display: none;
              `
            : ''}
      }
    }
  }

  ${({ staticTabs, breakpoint }) =>
    !staticTabs
      ? css`
          cursor: pointer;

          &:hover {
            background-color: #fcded0;
            box-shadow: 0 0 8px 0 rgba(0, 0, 0, 0.1);

            ${NavIcon} {
              opacity: 1;
            }
          }

          @media (${bp({ breakpoint })}) {
            opacity: 0;
            transition: opacity 1s ease-in-out;
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;

            &[data-active='true'] {
              opacity: 1;
            }

            ${CheckIcon} {
              font-size: 35px;
            }
          }
        `
      : css`
          @media (${bp({ breakpoint })}) {
            ${CheckIcon} {
              font-size: 35px;
            }
          }
        `}
`;

export const TabTextContainer = styled.div`
  display: flex;
  gap: 15px;
`;

export const TabText = styled.div`
  flex: 1;

  ${SmallHeading} {
    color: ${({ theme }) => theme.textDark};
    margin-bottom: 5px;
  }
`;

export const ProductBadge = styled.div<{
  color: ProductScreenBadge['color'];
  titleFontSize?: ProductScreenBadge['titleFontSize'];
  titleFontSizeMob?: ProductScreenBadge['titleFontSizeMob'];
  titleLineHeight?: ProductScreenBadge['titleLineHeight'];
}>`
  position: absolute;
  width: 150px;
  height: 150px;
  top: -40px;
  z-index: 3;
  box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.2);
  background-color: ${({ theme, color }) => theme[color]};
  color: ${({ theme }) => theme.white};
  border-radius: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 10px;
  box-shadow: 0 0 6px 0 rgba(0, 0, 0, 0.3);

  .badge-title {
    font-size: ${({ titleFontSize }) => titleFontSize || 40}px;
    font-weight: 800;
    line-height: ${({ titleLineHeight }) => titleLineHeight || 0.75};
  }

  .badge-text {
    font-size: 16px;
    line-height: 1;
  }

  @media (${mobileXXLargeMax}) {
    width: 100px;
    height: 100px;

    .badge-title {
      font-size: ${({ titleFontSizeMob }) => titleFontSizeMob || 25}px;
    }

    .badge-text {
      font-size: 12px;
    }
  }
`;

export const ButtonContainer = styled.div`
  margin-top: 25px;
  width: 100%;

  ${Button} {
    height: 54px;

    p {
      font-weight: 800;
    }
  }
`;

export const Content = styled.div<{
  reverse?: boolean;
  negMargin?: number;
  badgeXPos?: number;
  badgeXPosLap?: number;
  badgeXPosMob?: number;
  breakpoint?: number;
  mockupWidth?: number;
  tabsMt?: number;
  tabPadding?: number;
  tabHeight: number;
  staticTabs?: boolean;
  staticContent?: boolean;
  buttonXPadding?: number;
  buttonMt?: number;
}>`
  display: flex;
  flex-direction: ${({ reverse }) => (reverse ? 'row-reverse' : 'row')};

  ${TabContent} {
    @media (${({ breakpoint }) => bp({ breakpoint, min: true, fb: laptopMinXL })}) {
      ${({ reverse, tabPadding }) =>
        reverse
          ? css`
              padding-right: ${tabPadding || 80}px;
            `
          : css`
              padding-left: ${tabPadding || 80}px;
            `}
    }

    @media (${({ breakpoint }) => bp({ breakpoint, min: true })}) {
      ${({ reverse, negMargin }) =>
        reverse
          ? css`
              margin-right: -${negMargin || 10}px;
            `
          : css`
              margin-left: -${negMargin || 10}px;
            `}
    }
  }

  ${StaticContent} {
    @media (${({ breakpoint }) => bp({ breakpoint, min: true })}) {
      ${({ reverse, tabPadding }) =>
        reverse
          ? css`
              padding-left: 0px;
              padding-right: ${tabPadding || 0}px;
            `
          : css`
              padding-right: 0px;
              padding-left: ${tabPadding || 0}px;
            `}
    }

    @media (${({ breakpoint }) => bp({ breakpoint })}) {
      text-align: center;
    }
  }

  ${ProductBadge} {
    ${({ reverse, badgeXPos }) =>
      reverse
        ? css`
            right: ${badgeXPos || -40}px;
            transform: rotate(11deg);
          `
        : css`
            left: ${badgeXPos || -40}px;
            transform: rotate(-11deg);
          `}

    @media (${({ breakpoint }) => bp({ breakpoint, fb: laptopMaxXL })}) {
      ${({ reverse, badgeXPosLap }) =>
        reverse
          ? css`
              right: ${badgeXPosLap || -10}px;
            `
          : css`
              left: ${badgeXPosLap || -10}px;
            `}
    }
    @media (${({ breakpoint }) => bp({ breakpoint })}) {
      top: -8%;
      ${({ reverse, badgeXPosMob }) =>
        reverse
          ? css`
              right: ${badgeXPosMob || -15}px;
            `
          : css`
              left: ${badgeXPosMob || -15}px;
            `}
    }
  }

  ${TabsWrapper} {
    @media (${({ breakpoint }) => bp({ breakpoint })}) {
      max-width: 510px;
      margin: 0 auto;
      width: 100%;
      height: ${({ tabHeight, staticTabs }) => (staticTabs ? 'auto' : `${tabHeight + TAB_Y_PADDING * 2}px`)};
    }
  }

  ${MockupContainer} {
    max-width: ${({ mockupWidth }) => mockupWidth || 600}px;

    @media (${({ breakpoint }) => bp({ breakpoint, min: true })}) {
      flex-basis: ${({ mockupWidth }) => mockupWidth || 600}px;
    }
  }

  ${ButtonContainer} {
    @media (${({ breakpoint, reverse }) => bp({ breakpoint, min: true, fb: reverse ? largeTabletMin : laptopMinXL })}) {
      padding-left: ${({ reverse, staticContent, tabPadding, buttonXPadding }) =>
        buttonXPadding ||
        (reverse ? (staticContent ? 0 : 35) : staticContent && tabPadding != null ? tabPadding : 80)}px;
      margin-top: ${({ buttonMt }) => (buttonMt != null ? buttonMt : 25)}px;
    }

    @media (${({ breakpoint, reverse }) => bp({ breakpoint, fb: reverse ? largeTabletMax : laptopMaxXL })}) {
      margin: 25px auto;
      display: flex;
      justify-content: center;

      a {
        display: flex;
        justify-content: center;
        width: 100%;
      }
    }
  }

  ${TabsContainer} {
    margin-top: 15px;

    @media (${({ breakpoint }) => bp({ breakpoint, min: true })}) {
      margin-top: ${({ tabsMt }) => (tabsMt != null ? tabsMt : 15)}px;
    }
  }

  @media (${({ breakpoint }) => bp({ breakpoint })}) {
    flex-direction: column;
    gap: 20px;
  }
`;

export const SmalPrint = styled.div<{ mt?: number }>`
  margin-top: ${({ mt }) => mt || 120}px;
  text-align: center;
  font-size: 12px;
`;
